import { type Wallet } from "ethers";

import { type ChainIdValue } from "../config";
import { PRICE_SERVICE_URL } from "../env";
import { SwapError } from "./quote";

export class GaslessSwapError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "GaslessSwapError";
  }
}

export interface GaslessQuoteResponse {
  approval?: {
    type: string;
    hash: string;
    eip712: {
      types: Record<string, any[]>;
      domain: Record<string, any>;
      message: Record<string, any>;
      primaryType: string;
    };
  };
  trade: {
    type: string;
    hash: string;
    eip712: {
      types: Record<string, any[]>;
      domain: Record<string, any>;
      message: Record<string, any>;
      primaryType: string;
    };
  };
  blockNumber: string;
  buyAmount: string;
  buyToken: string;
  sellAmount: string;
  sellToken: string;
  chainId?: ChainIdValue;
  fees?: {
    integratorFee: {
      amount: string;
      token: string;
      type: string;
    } | null;
    zeroExFee: {
      amount: string;
      token: string;
      type: string;
    } | null;
    gasFee: {
      amount: string;
      token: string;
      type: string;
    } | null;
  };
  issues: {
    allowance: {
      actual: string;
      spender: string;
    };
    balance: any;
    simulationIncomplete: boolean;
    invalidSourcesPassed: string[];
  };
  liquidityAvailable: boolean;
  minBuyAmount: string;
  target: string;
  zid: string;
}

export interface GaslessSubmitResponse {
  tradeHash: string;
  type: string;
  zid: string;
}

export interface GaslessStatusResponse {
  status: "pending" | "succeeded" | "confirmed" | "failed";
  transactions?: Array<{
    hash: string;
    timestamp: number;
  }>;
  zid: string;
}

export interface GetGaslessQuoteParams {
  sellToken: string;
  buyToken: string;
  sellAmount: string;
  takerAddress: string;
  chainId: ChainIdValue;
  intentOnFilling?: string;
}

export async function getGaslessQuote({
  sellToken,
  buyToken,
  sellAmount,
  takerAddress,
  chainId,
  intentOnFilling = "false",
}: GetGaslessQuoteParams): Promise<GaslessQuoteResponse | null> {
  try {
    const params = new URLSearchParams({
      chainId,
      sellToken,
      buyToken,
      sellAmount,
      taker: takerAddress,
      intentOnFilling,
      gasless: "true",
    });

    const url = `${PRICE_SERVICE_URL}/0x-quote?${params.toString()}`;

    const response = await fetch(url);

    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage = `${response.status} ${response.statusText}`;
      try {
        const errorJson = JSON.parse(errorText);

        if (errorJson.message) {
          errorMessage = errorJson.message;
        }
      } catch (_parseError) {
        errorMessage = errorText;
      }

      throw new SwapError(errorMessage);
    }

    const data = await response.json();

    return data as GaslessQuoteResponse;
  } catch (error) {
    if (error instanceof SwapError) {
      throw error;
    }

    throw new SwapError(
      `Error fetching quote: ${error instanceof Error ? error.message : "Unknown error"}`
    );
  }
}

export async function submitGaslessSwap(
  approvalSignature: string | null,
  tradeSignature: string,
  approval: any,
  trade: any
): Promise<GaslessSubmitResponse | null> {
  try {
    // Parse the signatures back to objects
    const tradeSignatureObj = JSON.parse(tradeSignature);
    const approvalSignatureObj = approvalSignature ? JSON.parse(approvalSignature) : null;

    // Fix the domain chainId - convert string to number
    const domainChainId = parseInt(trade.eip712.domain.chainId, 10);

    if (isNaN(domainChainId)) {
      throw new GaslessSwapError(`Invalid chainId: ${trade.eip712.domain.chainId}`);
    }

    const fixedTradeEip712 = {
      ...trade.eip712,
      domain: {
        ...trade.eip712.domain,
        chainId: domainChainId,
      },
    };

    const payload: any = {
      chainId: domainChainId,
      trade: {
        type: trade.type,
        eip712: fixedTradeEip712,
        signature: tradeSignatureObj,
      },
    };

    if (approval && approvalSignatureObj) {
      const approvalChainId = parseInt(approval.eip712.domain.chainId, 10);

      if (isNaN(approvalChainId)) {
        throw new GaslessSwapError(`Invalid approval chainId: ${approval.eip712.domain.chainId}`);
      }

      const fixedApprovalEip712 = {
        ...approval.eip712,
        domain: {
          ...approval.eip712.domain,
          chainId: approvalChainId,
        },
      };

      payload.approval = {
        type: approval.type,
        eip712: fixedApprovalEip712,
        signature: approvalSignatureObj,
      };
    }

    const url = `${PRICE_SERVICE_URL}/0x-submit`;

    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error("Failed to submit gasless swap:", errorText);

      return null;
    }

    const data = await response.json();

    return data as GaslessSubmitResponse;
  } catch (error) {
    console.error("Error submitting gasless swap:", error);

    return null;
  }
}

export async function checkGaslessStatus(
  tradeHash: string,
  chainId: ChainIdValue = "1"
): Promise<GaslessStatusResponse | null> {
  try {
    const params = new URLSearchParams({
      chainId,
    });

    const url = `${PRICE_SERVICE_URL}/0x-status/${tradeHash}?${params.toString()}`;
    const response = await fetch(url);

    if (!response.ok) {
      console.error("Failed to check gasless status:", await response.text());

      return null;
    }

    const data = await response.json();

    return data as GaslessStatusResponse;
  } catch (error) {
    console.error("Error checking gasless status:", error);

    return null;
  }
}

export function splitSignature(signature: string): { v: number; r: string; s: string } {
  const sig = signature.slice(2); // Remove '0x'
  const r = "0x" + sig.slice(0, 64);
  const s = "0x" + sig.slice(64, 128);
  const v = parseInt(sig.slice(128, 130), 16);

  return { v, r, s };
}

export async function signGaslessMessage(
  wallet: Wallet,
  eip712Data: {
    types: Record<string, any[]>;
    domain: Record<string, any>;
    message: Record<string, any>;
    primaryType: string;
  }
): Promise<string> {
  try {
    // Filter out EIP712Domain from types as ethers.js handles it separately
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { EIP712Domain, ...customTypes } = eip712Data.types;

    // Sign the EIP-712 message with custom types only
    const signature = await wallet.signTypedData(
      eip712Data.domain,
      customTypes, // Pass custom types only, excluding EIP712Domain
      eip712Data.message
    );

    return signature;
  } catch (error) {
    console.error("Error signing gasless message:", error);
    throw new GaslessSwapError(
      `Failed to sign message: ${error instanceof Error ? error.message : "Unknown error"}`
    );
  }
}

export async function executeGaslessSwap(
  sellToken: string,
  buyToken: string,
  sellAmount: string,
  wallet: Wallet,
  chainId: ChainIdValue = "1"
): Promise<{ tradeHash: string; status: string }> {
  try {
    // Step 1: Get gasless quote
    const quote = await getGaslessQuote({
      sellToken,
      buyToken,
      sellAmount,
      takerAddress: wallet.address,
      chainId,
      intentOnFilling: "true",
    });

    if (!quote) {
      throw new GaslessSwapError("Failed to get gasless quote");
    }

    if (!quote.liquidityAvailable) {
      throw new GaslessSwapError("No liquidity available for this swap");
    }

    // Step 2: Sign approval if needed
    let approvalSignature: string | null = null;

    if (quote.approval) {
      const approvalSig = await signGaslessMessage(wallet, quote.approval.eip712);
      const splitApprovalSig = splitSignature(approvalSig);
      approvalSignature = {
        v: splitApprovalSig.v,
        r: splitApprovalSig.r,
        s: splitApprovalSig.s,
        signatureType: 2,
      } as any;
    }

    // Step 3: Sign trade message
    const tradeSig = await signGaslessMessage(wallet, quote.trade.eip712);
    const splitTradeSig = splitSignature(tradeSig);
    const tradeSignature = {
      v: splitTradeSig.v,
      r: splitTradeSig.r,
      s: splitTradeSig.s,
      signatureType: 2,
    };

    // Step 4: Submit the swap
    const submitResult = await submitGaslessSwap(
      approvalSignature ? JSON.stringify(approvalSignature) : null,
      JSON.stringify(tradeSignature),
      quote.approval,
      quote.trade
    );

    if (!submitResult) {
      throw new GaslessSwapError("Failed to submit gasless swap");
    }

    // Step 5: Check status
    let status = "pending";
    let attempts = 0;
    const maxAttempts = 60;

    while (attempts < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, 2000));

      const statusResult = await checkGaslessStatus(submitResult.tradeHash, chainId);

      if (statusResult) {
        status = statusResult.status;

        if (status === "succeeded" || status === "confirmed") {
          break;
        } else if (status === "failed") {
          throw new GaslessSwapError("Gasless swap failed");
        }
      }

      attempts++;
    }

    if (status !== "succeeded" && status !== "confirmed") {
      throw new GaslessSwapError("Gasless swap timed out");
    }

    return {
      tradeHash: submitResult.tradeHash,
      status,
    };
  } catch (error) {
    console.error("Error in executeGaslessSwap:", error);
    throw error instanceof GaslessSwapError
      ? error
      : new GaslessSwapError(
          `Gasless swap failed: ${error instanceof Error ? error.message : "Unknown error"}`
        );
  }
}
